import { create } from "zustand";
import { persist } from "zustand/middleware";
import { actorService } from "@/services/actorService";
import {
  Actor,
  CreateActorDto,
  UpdateActorDto,
  GetPaginationActorsDto,
  ExternalActor,
} from "@/types/actor";
import { showToast } from "@/lib/toast";

interface ActorState {
  // State
  actors: Actor[];
  externalActors: ExternalActor[];
  selectedActor: Actor | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  isLoadingDetail: boolean;
  isLoadingExternal: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isSyncing: boolean;
  error: string | null;

  // Actions
  fetchActors: (params?: GetPaginationActorsDto) => Promise<void>;
  fetchActorById: (id: string) => Promise<void>;
  fetchExternalActors: () => Promise<void>;
  createActor: (data: CreateActorDto) => Promise<void>;
  createActorWithImage: (
    data: CreateActorDto,
    imageFile?: File
  ) => Promise<boolean>;
  updateActor: (id: string, data: UpdateActorDto) => Promise<void>;
  updateActorWithImage: (
    id: string,
    data: UpdateActorDto,
    imageFile?: File
  ) => Promise<boolean>;
  deleteActor: (id: string) => Promise<void>;
  syncBulk: () => Promise<void>;
  syncSingle: (externalId: string) => Promise<void>;
  setSelectedActor: (actor: Actor | null) => void;
  clearSelectedActor: () => void;
  clearError: () => void;
  setCurrentPage: (page: number) => void;
}

export const useActorStore = create<ActorState>()(
  persist(
    (set, get) => ({
      // Initial state
      actors: [],
      externalActors: [],
      selectedActor: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },
      isLoading: false,
      isLoadingDetail: false,
      isLoadingExternal: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      isSyncing: false,
      error: null,

      // Fetch actors with pagination
      fetchActors: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response: any = await actorService.getPaginationActors(params);
          if (response.ok && response.data) {
            set({
              actors: response.data?.data?.data.actors,
              pagination: response.data?.data?.data.pagination,
              isLoading: false,
            });
          } else {
            set({
              error: response.message || "Lỗi khi tải danh sách diễn viên",
              isLoading: false,
            });
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tải danh sách diễn viên",
            isLoading: false,
          });
        }
      },

      // Fetch actor by ID
      fetchActorById: async (id: string) => {
        set({ isLoadingDetail: true, error: null });
        try {
          const response: any = await actorService.getActorById(id);
          if (response.ok && response.data) {
            set({
              selectedActor: response.data?.data?.data,
              isLoadingDetail: false,
            });
          } else {
            set({
              error: response.message || "Lỗi khi tải diễn viên",
              isLoadingDetail: false,
            });
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tải diễn viên",
            isLoadingDetail: false,
          });
        }
      },

      // Fetch external actors
      fetchExternalActors: async () => {
        set({ isLoadingExternal: true, error: null });
        try {
          const response: any = await actorService.getExternalActors();
          if (response.ok && response.data) {
            set({
              externalActors: response.data?.data?.data,
              isLoadingExternal: false,
            });
          } else {
            set({
              error:
                response.message ||
                "Lỗi khi tải danh sách diễn viên từ external DB",
              isLoadingExternal: false,
            });
          }
        } catch (error: any) {
          set({
            error:
              error.message ||
              "Lỗi khi tải danh sách diễn viên từ external DB",
            isLoadingExternal: false,
          });
        }
      },

      // Create actor
      createActor: async (data: CreateActorDto) => {
        set({ isCreating: true, error: null });
        try {
          const response: any = await actorService.create(data);
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchActors({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isCreating: false });
            showToast.success("Tạo diễn viên thành công");
          } else {
            set({
              error: response.message || "Lỗi khi tạo diễn viên",
              isCreating: false,
            });
            showToast.error(response.message || "Lỗi khi tạo diễn viên");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tạo diễn viên",
            isCreating: false,
          });
          showToast.error(error.message || "Lỗi khi tạo diễn viên");
        }
      },

      // Create actor with image upload
      createActorWithImage: async (
        data: CreateActorDto,
        imageFile?: File
      ): Promise<boolean> => {
        set({ isCreating: true, error: null });
        try {
          const response: any = await actorService.createWithImage(
            data,
            imageFile
          );
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchActors({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isCreating: false });
            showToast.success("Tạo diễn viên thành công");
            return true;
          } else {
            set({
              error: response.message || "Lỗi khi tạo diễn viên",
              isCreating: false,
            });
            showToast.error(
              response.data?.message || response.message || "Lỗi khi tạo diễn viên"
            );
            return false;
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tạo diễn viên",
            isCreating: false,
          });
          showToast.error(error.message || "Lỗi khi tạo diễn viên");
          return false;
        }
      },

      // Update actor
      updateActor: async (id: string, data: UpdateActorDto) => {
        set({ isUpdating: true, error: null });
        try {
          const response: any = await actorService.updateActor(id, data);
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchActors({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isUpdating: false });
            showToast.success("Cập nhật diễn viên thành công");
          } else {
            set({
              error: response.message || "Lỗi khi cập nhật diễn viên",
              isUpdating: false,
            });
            showToast.error(response.message || "Lỗi khi cập nhật diễn viên");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi cập nhật diễn viên",
            isUpdating: false,
          });
          showToast.error(error.message || "Lỗi khi cập nhật diễn viên");
        }
      },

      // Update actor with image upload
      updateActorWithImage: async (
        id: string,
        data: UpdateActorDto,
        imageFile?: File
      ): Promise<boolean> => {
        set({ isUpdating: true, error: null });
        try {
          const response: any = await actorService.updateWithImage(
            id,
            data,
            imageFile
          );
          if (response.ok && response.data) {
            set({
              isUpdating: false,
              selectedActor: response.data?.data?.data,
            });
            showToast.success("Cập nhật diễn viên thành công");
            return true;
          } else {
            set({
              error: response.message || "Lỗi khi cập nhật diễn viên",
              isUpdating: false,
            });
            showToast.error(
              response.data?.message ||
                response.message ||
                "Lỗi khi cập nhật diễn viên"
            );
            return false;
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi cập nhật diễn viên",
            isUpdating: false,
          });
          showToast.error(error.message || "Lỗi khi cập nhật diễn viên");
          return false;
        }
      },

      // Delete actor
      deleteActor: async (id: string) => {
        set({ isDeleting: true, error: null });
        try {
          const response: any = await actorService.deleteActor(id);
          if (response.ok) {
            const { pagination } = get();
            await get().fetchActors({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isDeleting: false, selectedActor: null });
            showToast.success("Xóa diễn viên thành công");
          } else {
            set({
              error: response.message || "Lỗi khi xóa diễn viên",
              isDeleting: false,
            });
            showToast.error(response.message || "Lỗi khi xóa diễn viên");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi xóa diễn viên",
            isDeleting: false,
          });
          showToast.error(error.message || "Lỗi khi xóa diễn viên");
        }
      },

      // Sync bulk from external DB
      syncBulk: async () => {
        set({ isSyncing: true, error: null });
        try {
          const response: any = await actorService.syncBulk();
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchActors({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isSyncing: false });
            const data = response.data?.data?.data;
            showToast.success(
              `Sync thành công: ${data?.created} tạo mới, ${data?.updated} cập nhật`
            );
          } else {
            set({
              error: response.message || "Lỗi khi sync diễn viên",
              isSyncing: false,
            });
            showToast.error(response.message || "Lỗi khi sync diễn viên");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi sync diễn viên",
            isSyncing: false,
          });
          showToast.error(error.message || "Lỗi khi sync diễn viên");
        }
      },

      // Sync single from external DB
      syncSingle: async (externalId: string) => {
        set({ isSyncing: true, error: null });
        try {
          const response: any = await actorService.syncSingle(externalId);
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchActors({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isSyncing: false });
            showToast.success("Sync diễn viên thành công");
          } else {
            set({
              error: response.message || "Lỗi khi sync diễn viên",
              isSyncing: false,
            });
            showToast.error(response.message || "Lỗi khi sync diễn viên");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi sync diễn viên",
            isSyncing: false,
          });
          showToast.error(error.message || "Lỗi khi sync diễn viên");
        }
      },

      // Utilities
      setSelectedActor: (actor: Actor | null) => set({ selectedActor: actor }),
      clearSelectedActor: () => set({ selectedActor: null }),
      clearError: () => set({ error: null }),
      setCurrentPage: (page: number) =>
        set((state) => ({
          pagination: { ...state.pagination, page },
        })),
    }),
    {
      name: "actor-storage",
      partialize: (state) => ({ pagination: state.pagination }),
    }
  )
);
